import { createSlice, PayloadAction } from "@reduxjs/toolkit";
import { AppDispatch, AppThunk } from "@/app/store";
import {
  listLibraryModules,
  ListLibraryModulesRequest,
} from "@/modules/learningCenter/features/ModuleLibrary/api/listLibraryModules";
import { publishLibraryModule } from "@/modules/learningCenter/features/ModuleLibrary/api/publishLibraryModule";
import {
  archiveLibraryModule,
  ArchiveLibraryModuleResponse,
} from "@/modules/learningCenter/features/ModuleLibrary/api/archiveLibraryModule";
import { LibraryModuleListItem } from "@/modules/learningCenter/features/ModuleLibrary/types/libraryModule";

interface LibraryModuleListState {
  modules: LibraryModuleListItem[];
  totalCount: number;

  fetchLoading: boolean;
  fetchError: string | null;

  publishLoading: boolean;
  archiveLoading: boolean;
}

const initialState: LibraryModuleListState = {
  modules: [],
  totalCount: 0,

  fetchLoading: false,
  fetchError: null,

  publishLoading: false,
  archiveLoading: false,
};

const libraryModuleListSlice = createSlice({
  name: "libraryModuleList",
  initialState,
  reducers: {
    setFetchLoading(state, action: PayloadAction<boolean>) {
      state.fetchLoading = action.payload;
    },
    setFetchError(state, action: PayloadAction<string | null>) {
      state.fetchError = action.payload;
    },
    setModules(state, action: PayloadAction<LibraryModuleListItem[]>) {
      state.modules = action.payload;
    },
    setTotalCount(state, action: PayloadAction<number>) {
      state.totalCount = action.payload;
    },
    setPublishLoading(state, action: PayloadAction<boolean>) {
      state.publishLoading = action.payload;
    },
    setArchiveLoading(state, action: PayloadAction<boolean>) {
      state.archiveLoading = action.payload;
    },
  },
});

export const {
  setFetchLoading,
  setFetchError,
  setModules,
  setTotalCount,
  setPublishLoading,
  setArchiveLoading,
} = libraryModuleListSlice.actions;

export default libraryModuleListSlice.reducer;

export const fetchLibraryModulesAction =
  (params: ListLibraryModulesRequest): AppThunk =>
  async (dispatch: AppDispatch) => {
    dispatch(setFetchLoading(true));
    dispatch(setFetchError(null));
    try {
      const response = await listLibraryModules(params);
      dispatch(setModules(response.data));
      dispatch(
        setTotalCount(response.meta?.totalCount ?? response.data.length),
      );
    } finally {
      dispatch(setFetchLoading(false));
    }
  };

export const publishLibraryModuleAction =
  (uuid: string, onSuccess?: () => void): AppThunk =>
  async (dispatch: AppDispatch) => {
    dispatch(setPublishLoading(true));
    try {
      await publishLibraryModule(uuid);
      if (onSuccess) {
        onSuccess();
      }
    } finally {
      dispatch(setPublishLoading(false));
    }
  };

export const archiveLibraryModuleAction =
  (
    uuid: string,
    onSuccess?: (result: ArchiveLibraryModuleResponse["data"]) => void,
  ): AppThunk =>
  async (dispatch: AppDispatch) => {
    dispatch(setArchiveLoading(true));
    try {
      const response = await archiveLibraryModule(uuid);
      if (onSuccess) {
        onSuccess(response.data);
      }
    } finally {
      dispatch(setArchiveLoading(false));
    }
  };
